import { useState, useEffect } from 'react'

/**
 * Custom hook for responsive behavior based on media queries
 * @param query - CSS media query string
 * @returns boolean indicating if the media query matches
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false)

  useEffect(() => {
    const media = window.matchMedia(query)

    // Set initial value
    if (media.matches !== matches) {
      setMatches(media.matches)
    }

    // Create listener for changes
    const listener = () => setMatches(media.matches)

    // Modern browsers support addEventListener
    media.addEventListener('change', listener)

    return () => media.removeEventListener('change', listener)
  }, [matches, query])

  return matches
}

/**
 * Preset breakpoint hooks following Tailwind CSS conventions
 */

// Mobile devices (< 768px)
export const useIsMobile = () => useMediaQuery('(max-width: 767px)')

// Tablet devices (768px - 1023px)
export const useIsTablet = () => useMediaQuery('(min-width: 768px) and (max-width: 1023px)')

// Desktop devices (>= 1024px)
export const useIsDesktop = () => useMediaQuery('(min-width: 1024px)')

// Small mobile devices (< 640px)
export const useIsSmallMobile = () => useMediaQuery('(max-width: 639px)')

// Large desktop devices (>= 1280px)
export const useIsLargeDesktop = () => useMediaQuery('(min-width: 1280px)')
